// Locale-ready Hub copy. Structured as templates[locale][key] so adding
// Spanish (or any locale) later is a data addition, not a refactor.
// PR2 ships English only; `resolveLocale` always returns 'en' for now.
//
// Number/metric formatting lives in humanize.ts; this module holds the
// mom-test sentence templates that would need translation.

export type Locale = 'en';

export const DEFAULT_LOCALE: Locale = 'en';

type NoteSurface = 'today' | 'growth' | 'project';

// Verifiability principle (PR2.5): every outcome template says ONLY what is
// directly observable in the row. If a placeholder's data is missing, the
// template falls back to a version WITHOUT that fragment — it never invents
// vague filler ("people are seeing it"). Owner-questionable claims (intent,
// scheduling, hunches) were stripped during the mapping review.
export type OutcomeRender = { text: string } | null;

export type OutcomeInput = {
  n: number;
  meta: Record<string, unknown>;
};

export type OutcomeTemplate = (input: OutcomeInput) => OutcomeRender;

type HubCopy = {
  note: Record<NoteSurface, string>;
  noteEmpty: Record<NoteSurface, string>;
  trend: Record<'better' | 'about_same' | 'softer', string>;
  outcomes: Record<string, OutcomeTemplate>;
  outcomesEmpty: string;
  staleness: (input: { name: string; days: number }) => string;
  stalenessVerb: string;
};

// ── helpers ─────────────────────────────────────────────────────────
const s = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length === 0 ? null : t;
};
const num = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim().length > 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};
const pl = (n: number, singular: string, plural: string) => (n === 1 ? singular : plural);

// ── English copy ───────────────────────────────────────────────────
const EN: HubCopy = {
  note: {
    today: 'It’s been {em} since you set Hunter up. We’re getting good at this together.',
    growth: 'Your business has been growing with Hunter for {em}. Keep going.',
    project: 'Hunter started knowing nothing {em} ago. Look how far we’ve come.',
  },
  noteEmpty: {
    today: 'Day 1. The more you use me, the better I get at your business.',
    growth: 'As your business grows, this is where you’ll see it.',
    project: 'Right now Hunter knows nothing. That’s about to change.',
  },
  trend: {
    better: 'That’s better than last month.',
    about_same: 'About the same as last month.',
    softer: 'A little softer than last month.',
  },

  // T1–T8 + T10 (T9 staleness moved to priority-stack). Every template is
  // a function so it can drop fragments cleanly when data is missing.
  outcomes: {
    // T1
    'inbox.ai_responded': ({ n }) =>
      n > 0
        ? { text: `Replied to ${n} ${pl(n, 'message', 'messages')} while you were busy.` }
        : null,
    // T2
    'studio.draft_published': ({ n, meta }) => {
      const title = s(meta.title);
      const channel = s(meta.channel);
      const views = num(meta.views);
      const saves = num(meta.saves);

      if (!title && !channel) return n > 0 ? { text: `Posted ${n} ${pl(n, 'piece', 'pieces')}.` } : null;
      const lead = title && channel
        ? `Posted "${title}" on ${channel}.`
        : title
        ? `Posted "${title}".`
        : `Posted on ${channel}.`;

      const stats: string[] = [];
      if (views !== null) stats.push(`${views} ${pl(views, 'person', 'people')} saw it`);
      if (saves !== null) stats.push(`${saves} saved it`);
      return { text: stats.length ? `${lead} ${stats.join(', ')}.` : lead };
    },
    // T3 (ALA apps / quizzes — pages.conversion_recorded counts completions)
    'pages.conversion_recorded': ({ n, meta }) => {
      const name = s(meta.name) ?? s(meta.app_name);
      const window = s(meta.window) === 'week' ? 'this week' : 'today';
      if (!name) return n > 0 ? { text: `${n} ${pl(n, 'person', 'people')} used something Hunter built ${window}.` } : null;
      return { text: `Built ${name}. ${n} ${pl(n, 'person', 'people')} did it ${window}.` };
    },
    // T4
    'campaign.message_sent': ({ n, meta }) => {
      const when = s(meta.window) ?? 'today';
      return n > 0
        ? { text: `Followed up with ${n} ${pl(n, 'lead', 'leads')} ${when}.` }
        : null;
    },
    // T5
    'studio.draft_generated': ({ n }) =>
      n > 0
        ? { text: `Wrote ${n} article ${pl(n, 'draft', 'drafts')} for you.` }
        : null,
    // T6
    'hunt.signal_detected': ({ n, meta }) => {
      const context = s(meta.context);
      return n > 0
        ? {
            text: context
              ? `Found ${n} ${pl(n, 'person', 'people')} ${context} interested in what you do.`
              : `Found ${n} ${pl(n, 'person', 'people')} interested in what you do.`,
          }
        : null;
    },
    // T7
    'products.delivery_sent': ({ n, meta }) => {
      const name = s(meta.product_name) ?? s(meta.name);
      if (!name) return n > 0 ? { text: `Delivered to ${n} ${pl(n, 'customer', 'customers')}.` } : null;
      return { text: `Delivered ${name} to ${n} ${pl(n, 'customer', 'customers')}.` };
    },
    // T8 — the key line
    'campaign.converted': ({ n }) =>
      n > 0
        ? {
            text:
              n === 1
                ? `1 person became a customer today.`
                : `${n} people became customers today.`,
          }
        : null,
    // T10
    'inbox.human_took_over': ({ n }) =>
      n > 0
        ? {
            text: `Handed ${n} ${pl(n, 'conversation', 'conversations')} back to you to handle yourself.`,
          }
        : null,
  },
  outcomesEmpty: 'Quiet day. Hunter is watching.',

  staleness: ({ name, days }) =>
    `${name} hasn’t replied in ${days} ${pl(days, 'day', 'days')}. Want me to try again or let it go?`,
  stalenessVerb: 'try again',
};

const TEMPLATES: Record<Locale, HubCopy> = { en: EN };

/**
 * Resolve the active locale. PR2: always 'en'. When ES lands, derive from
 * `organizations.settings.language` (default 'es' in schema) or a request
 * header — this is the single place that decision is made.
 */
export function resolveLocale(_orgLanguage?: string | null): Locale {
  return DEFAULT_LOCALE;
}

export function hubCopy(locale: Locale = DEFAULT_LOCALE): HubCopy {
  return TEMPLATES[locale] ?? EN;
}
